import dynamic from "next/dynamic";
import { fetchSingleTypeData } from "@/services/CmsService";
import { fetchCommonData } from "@/services/CommonService";
import usePopupVisibility from "@/components/PopupForm/usePopupVisibility";
import { useState, useEffect, useRef } from "react";
import Head from "next/head";

const PopupForm = dynamic(() => import("@/components/PopupForm/PopupForm"), {
  ssr: false,
});

const HeroBanner = dynamic(() => import("@/components/HomeBanner/HomeBanner"), {
  loading: () => <div style={{ minHeight: "80vh", background: "#f5f5f5" }} />,
  ssr: true,
});

const TransformativeHome = dynamic(
  () => import("@/components/TransformativeHome/TransformativeHome"),
  {
    loading: () => (
      <div style={{ minHeight: "300px", background: "#f5f5f5" }} />
    ),
  }
);

const ExploreFuture = dynamic(
  () => import("@/components/ExploreFuture/ExploreFuture"),
  {
    loading: () => (
      <div style={{ minHeight: "300px", background: "#f5f5f5" }} />
    ),
  }
);

const AccreditationHome = dynamic(
  () => import("@/components/AccreditationHome/AccreditationHome"),
  { ssr: false }
);

const StudentFunding = dynamic(
  () => import("@/components/StudentFunding/StudentFunding"),
  { ssr: false }
);

const InsightsHighlights = dynamic(
  () => import("@/components/InsightsHighlights/InsightsHighlights"),
  { ssr: false }
);

const HomeTestimonials = dynamic(
  () => import("@/components/HomeTestimonials/HomeTestimonials"),
  { ssr: false }
);

const ThroughResearch = dynamic(
  () => import("@/components/ThroughResearch/ThroughResearch"),
  { ssr: false }
);

const StickyFooter = dynamic(
  () => import("@/components/StickyFooter/StickyFooter"),
  {
    ssr: false,
    loading: () => <div style={{ height: "60px", background: "#ddd" }} />,
  }
);
const YoutubeVidioPopup = dynamic(
  () => import("@/components/YoutubeVidioPopup/YoutubeVidioPopup"),
  { ssr: false }
);

const StudentLife = dynamic(
  () => import("@/components/StudentLife/StudentLife"),
  { ssr: false }
);

const Layout = dynamic(() => import("@/components/Layout/Layout"), {
  ssr: false,
});

interface HomeProps {
  cmsHomePage: any | null;
  commonData: any | null;
}

export default function Home({ cmsHomePage, commonData }: HomeProps) {
  const {
    isPopupVisible,
    hidePopup,
    iscrollPopupVisible,
    scrollshowPopup,
    scrollHidePopup,
  } = usePopupVisibility();
  const [isYoutubePopup, setIsisYoutubePopup] = useState(false);
  const [isFooterLoaded, setIsFooterLoaded] = useState(false);
  const [showBelowFold, setShowBelowFold] = useState(false);
  const [hasMounted, setHasMounted] = useState(false);
  const screenShowRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    const timer = setTimeout(() => setIsFooterLoaded(true), 3000);
    return () => clearTimeout(timer);
  }, []);

  const popupTiming =
    (cmsHomePage?.popup_section?.popup_visible_time_in_sec || 5) * 1000;

  useEffect(() => {
    const isPopupShown = localStorage.getItem("popupShown");
    if (isPopupShown) return;

    const handleScroll = () => {
      const experienceSection = document.getElementById("experience-section");
      if (!experienceSection) return;

      const { top } = experienceSection.getBoundingClientRect();
      if (top < window.innerHeight * 0.5) {
        setTimeout(() => {
          scrollshowPopup();
          localStorage.setItem("popupShown", "true");
        }, popupTiming);

        window.removeEventListener("scroll", handleScroll);
      }
    };

    window.addEventListener("scroll", handleScroll);

    return () => window.removeEventListener("scroll", handleScroll);
  }, [popupTiming]);

  const handleClosePopup = () => {
    hidePopup();
    scrollHidePopup();
  };

  const handleYoutubePopupClose = () => {
    setIsisYoutubePopup(false);
  };

  const {
    home_banner,
    transformative_learning,
    explore_your_future,
    accreditations,
    testimonial,
    through_research,
    student_funding,
    insights_and_highlights,
    student_life,
    sticky_footer,
  } = cmsHomePage || {};

  const StickyFooterComponent = sticky_footer?.is_sticky_footer_enabled
    ? sticky_footer
    : commonData?.sticky_footer?.is_sticky_footer_enabled
    ? commonData?.sticky_footer
    : null;

  useEffect(() => {
    setHasMounted(true);
    const onScroll = () => {
      if (window.scrollY > 100) {
        setShowBelowFold(true);
        window.removeEventListener("scroll", onScroll);
      }
    };

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setShowBelowFold(true);
          observer.disconnect();
        }
      },
      {
        rootMargin: "0px",
        threshold: 0.1,
      }
    );

    if (screenShowRef.current) {
      observer.observe(screenShowRef.current);
    }

    window.addEventListener("scroll", onScroll);

    return () => {
      window.removeEventListener("scroll", onScroll);
      observer.disconnect();
    };
  }, []);

  if (!hasMounted) return null;

  return (
    <>
      <Head>
        {cmsHomePage?.home_banner?.sliders[0] && (
          <>
            <link
              rel="preload"
              as="image"
              href={cmsHomePage?.home_banner?.sliders[0]?.desktop_img.url}
              fetchPriority="high"
              imageSrcSet={`
                ${cmsHomePage?.home_banner?.sliders[0]?.mobile_img.url} 480w,
                ${cmsHomePage?.home_banner?.sliders[0]?.desktop_img.url} 1920w
              `}
              imageSizes="100vw"
            />
            <link rel="preconnect" href="https://test-cdn.regenesys.digital" />
          </>
        )}

        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "WebPage",
            name: cmsHomePage?.seo_data?.meta_title,
            description: cmsHomePage?.seo_data?.meta_description,
          })}
        </script>
      </Head>

      <Layout
        SeoData={cmsHomePage?.seo_data}
        pageName="home"
        commonData={commonData}
      >
        {isPopupVisible && (
          <PopupForm
            isVisible={isPopupVisible}
            onClose={handleClosePopup}
            // isProgramPage={studyMode}
          />
        )}

        {iscrollPopupVisible && (
          <PopupForm
            isVisible={iscrollPopupVisible}
            onClose={handleClosePopup}
            redirectlink={
              cmsHomePage?.popup_section?.common_popup?.title_link?.link?.url
            }
            // isProgramPage={studyMode}
            popUpData={cmsHomePage?.popup_section}
          />
        )}

        {isYoutubePopup && (
          <YoutubeVidioPopup
            isVisibleVideo={isYoutubePopup}
            youtubeVideoLink={""}
            onClose={handleYoutubePopupClose}
          />
        )}
        {home_banner && <HeroBanner data={home_banner} />}
        {transformative_learning && (
          <TransformativeHome data={transformative_learning} />
        )}
        {explore_your_future && <ExploreFuture data={explore_your_future} />}

        {showBelowFold && (
          <>
            {accreditations && <AccreditationHome data={accreditations} />}
            {testimonial && <HomeTestimonials data={testimonial} />}
            <div id="experience-section"></div>

            {through_research && <ThroughResearch data={through_research} />}
            {student_funding && <StudentFunding data={student_funding} />}
            {student_life && <StudentLife data={student_life} />}
            {insights_and_highlights && (
              <InsightsHighlights data={insights_and_highlights} />
            )}
            {isFooterLoaded && StickyFooterComponent && (
              <StickyFooter data={StickyFooterComponent} />
            )}
          </>
        )}
      </Layout>
    </>
  );
}

export async function getServerSideProps() {
  try {
    const [cmsHomePage, commonData] = await Promise.all([
      fetchSingleTypeData(),
      fetchCommonData(),
    ]);

    return {
      props: {
        cmsHomePage: cmsHomePage?.data || null,
        commonData: commonData?.data || null,
      },
    };
  } catch (error) {
    console.error("Error fetching CMS data:", error);

    return {
      props: {
        cmsHomePage: null,
        commonData: null,
      },
    };
  }
}
